import axios from 'axios';
import { 
  PlanogramTask, 
  TaskDetail, 
  TaskSearchParams, 
  ApiResponse, 
  ListResponse,
  TaskStatistics,
  MoveOutDetail,
  MoveInDetail,
  RearrangeDetail,
  NewItemDetail,
  OperationOverview,
  TaskStatus,
  Priority,
  PauseReason,
  Feedback
} from '../pages/task-management/task';
import { 
  mockTasks, 
  mockTaskDetails, 
  mockStatistics,
  mockOptions,
  createMockResponse,
  createMockListResponse,
  mockDelay
} from '../mock/mockTallyTaskData';
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import { WHOLE_USE_MOCK } from './api';
import httpClient from './httpClient';

// 是否使用模拟数据
const USE_MOCK_DATA = true;

// 配置axios实例
const api = axios.create({
  baseURL: '/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 添加认证token等
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

export class TaskService {
  // 获取任务列表
  static async getTaskList(params: TaskSearchParams): Promise<ApiResponse<ListResponse<PlanogramTask>>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay();
      
      // 模拟搜索过滤
      let filteredTasks = [...mockTasks];
      
      if (params.taskCode) {
        filteredTasks = filteredTasks.filter(task => 
          task.taskCode.toLowerCase().includes(params.taskCode!.toLowerCase())
        );
      }
      
      // Removed taskName filter
      
      if (params.shop) {
        filteredTasks = filteredTasks.filter(task => task.shop === params.shop);
      }
      
      if (params.displayGroup) {
        filteredTasks = filteredTasks.filter(task => task.displayGroup === params.displayGroup);
      }
      
      if (params.planogramPlan) {
        filteredTasks = filteredTasks.filter(task => task.planogramPlan === params.planogramPlan);
      }
      
      if (params.status) {
        filteredTasks = filteredTasks.filter(task => task.status === params.status);
      }
      
      if (params.planType) {
        filteredTasks = filteredTasks.filter(task => task.planType === params.planType);
      }
      
      if (params.priority) {
        filteredTasks = filteredTasks.filter(task => task.priority === params.priority);
      }
      
      if (params.assignTo) {
        filteredTasks = filteredTasks.filter(task => task.assignTo === params.assignTo);
      }
      
      // 添加日期和时间过滤
      if (params.planStartDate) {
        const [startDate, endDate] = params.planStartDate;
        filteredTasks = filteredTasks.filter(task => {
          const taskDate = task.planStartDate;
          return taskDate >= startDate && taskDate <= endDate;
        });
      }
      
      if (params.planEndDate) {
        const [startDate, endDate] = params.planEndDate;
        filteredTasks = filteredTasks.filter(task => {
          const taskDate = task.planEndDate;
          return taskDate >= startDate && taskDate <= endDate;
        });
      }
      
      if (params.generateTime) {
        const [startTime, endTime] = params.generateTime;
        filteredTasks = filteredTasks.filter(task => {
          const taskTime = task.generateTime;
          return taskTime >= startTime && taskTime <= endTime;
        });
      }
      
      if (params.completeTime) {
        const [startTime, endTime] = params.completeTime;
        filteredTasks = filteredTasks.filter(task => {
          const taskTime = task.completeTime;
          return taskTime && taskTime >= startTime && taskTime <= endTime;
        });
      }
      
      // 模拟分页
      const { currentPage, pageSize } = params;
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedTasks = filteredTasks.slice(startIndex, endIndex);
      
      return createMockResponse(
        createMockListResponse(paginatedTasks, currentPage, pageSize, filteredTasks.length)
      );
    }
    
    let resp = await httpClient.post(API_ENDPOINTS.TASKS.PAGE, params);
    return resp.data;
  }

  static async getTaskList4Store(params: TaskSearchParams): Promise<ApiResponse<ListResponse<PlanogramTask>>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay();
      
      // 模拟搜索过滤
      let filteredTasks = [...mockTasks];
      
      if (params.taskCode) {
        filteredTasks = filteredTasks.filter(task => 
          task.taskCode.toLowerCase().includes(params.taskCode!.toLowerCase())
        );
      }
      
      // Removed taskName filter
      
      if (params.shop) {
        filteredTasks = filteredTasks.filter(task => task.shop === params.shop);
      }
      
      if (params.displayGroup) {
        filteredTasks = filteredTasks.filter(task => task.displayGroup === params.displayGroup);
      }
      
      if (params.planogramPlan) {
        filteredTasks = filteredTasks.filter(task => task.planogramPlan === params.planogramPlan);
      }
      
      if (params.status) {
        filteredTasks = filteredTasks.filter(task => task.status === params.status);
      }
      
      if (params.planType) {
        filteredTasks = filteredTasks.filter(task => task.planType === params.planType);
      }
      
      if (params.priority) {
        filteredTasks = filteredTasks.filter(task => task.priority === params.priority);
      }
      
      if (params.assignTo) {
        filteredTasks = filteredTasks.filter(task => task.assignTo === params.assignTo);
      }
      
      // 添加日期和时间过滤
      if (params.planStartDate) {
        const [startDate, endDate] = params.planStartDate;
        filteredTasks = filteredTasks.filter(task => {
          const taskDate = task.planStartDate;
          return taskDate >= startDate && taskDate <= endDate;
        });
      }
      
      if (params.planEndDate) {
        const [startDate, endDate] = params.planEndDate;
        filteredTasks = filteredTasks.filter(task => {
          const taskDate = task.planEndDate;
          return taskDate >= startDate && taskDate <= endDate;
        });
      }
      
      if (params.generateTime) {
        const [startTime, endTime] = params.generateTime;
        filteredTasks = filteredTasks.filter(task => {
          const taskTime = task.generateTime;
          return taskTime >= startTime && taskTime <= endTime;
        });
      }
      
      if (params.completeTime) {
        const [startTime, endTime] = params.completeTime;
        filteredTasks = filteredTasks.filter(task => {
          const taskTime = task.completeTime;
          return taskTime && taskTime >= startTime && taskTime <= endTime;
        });
      }
      
      // 模拟分页
      const { currentPage, pageSize } = params;
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedTasks = filteredTasks.slice(startIndex, endIndex);
      
      return createMockResponse(
        createMockListResponse(paginatedTasks, currentPage, pageSize, filteredTasks.length)
      );
    }
    
    let resp = await httpClient.post(API_ENDPOINTS.TASKS.PAGE_BY_STORE, params);
    return resp.data;
  }

  // 获取任务明细
  static async getTaskDetails(taskCode: string): Promise<ApiResponse<TaskDetail[]>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(300);
      const details = mockTaskDetails[taskCode] || [];
      return createMockResponse(details);
    }
    let resp = await httpClient.get(API_ENDPOINTS.TASKS.DETAILS + '?taskCode=' + taskCode);
    return resp.data;
  }

  // 获取任务统计
  static async getTaskStatistics(): Promise<ApiResponse<TaskStatistics>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(200);
      // 动态计算统计数据
        const stats = {
          totalTasks: mockTasks.length,
          notStartedTasks: mockTasks.filter(task => task.status === 'Not Started').length,
          inProgressTasks: mockTasks.filter(task => task.status === 'In Progress').length,
          pausedTasks: mockTasks.filter(task => task.status === 'Paused').length,
          completedTasks: mockTasks.filter(task => task.status === 'Completed').length,
        };
        return createMockResponse(stats);
    }
    let resp = await httpClient.post('/planogram-tasks/statistics', {});
    return resp.data;
  }

  // 获取商品清单数据
  static async getProductList(storeCode: string, taskCode: string): Promise<ApiResponse<TaskDetail[]>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(300);
      const details = mockTaskDetails[taskCode] || [];
      return createMockResponse(details);
    }
    
    // 使用已有的 DETAILS 端点获取任务明细
    let resp = await httpClient.get(API_ENDPOINTS.TASKS.DETAILS + '?warehouseCode='+ storeCode + '&orderCode=' + taskCode);
    return resp.data;
  }

  // 获取门店列表（用于下拉选择）
  static async getShopList(): Promise<ApiResponse<{ tableData: Array<{ value: string; label: string }> }>> {
    if (USE_MOCK_DATA) {
      await mockDelay(200);
      return createMockResponse({ tableData: mockOptions.shops });
    }
    
    return api.post('/shops/list', { pageSize: 1000 });
  }

  // 获取Display Group列表
  static async getDisplayGroupList(shopCode?: string): Promise<ApiResponse<{ tableData: Array<{ value: string; label: string }> }>> {
    if (USE_MOCK_DATA) {
      await mockDelay(200);
      const displayGroups = shopCode && mockOptions.displayGroups[shopCode as keyof typeof mockOptions.displayGroups] 
        ? mockOptions.displayGroups[shopCode as keyof typeof mockOptions.displayGroups]
        : [];
      return createMockResponse({ tableData: displayGroups });
    }
    
    return api.post('/display-groups/list', { shopCode, pageSize: 1000 });
  }

  // 获取货架列表
  static async getShelfList(displayGroupCode?: string): Promise<ApiResponse<{ tableData: Array<{ value: string; label: string }> }>> {
    if (USE_MOCK_DATA) {
      await mockDelay(200);
      const shelves = displayGroupCode && mockOptions.shelves[displayGroupCode as keyof typeof mockOptions.shelves]
        ? mockOptions.shelves[displayGroupCode as keyof typeof mockOptions.shelves]
        : [];
      return createMockResponse({ tableData: shelves });
    }
    
    return api.post('/shelves/list', { displayGroupCode, pageSize: 1000 });
  }

  // 获取陈列计划列表
  static async getPlanogramPlanList(): Promise<ApiResponse<{ tableData: Array<{ value: string; label: string }> }>> {
    if (USE_MOCK_DATA) {
      await mockDelay(200);
      return createMockResponse({ tableData: mockOptions.planogramPlans });
    }
    
    return api.post('/planogram-plans/list', { pageSize: 1000 });
  }

  // 获取作业概览
  static async getOperationOverview(shop: string, taskCode: string): Promise<ApiResponse<Record<string, OperationOverview>>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(500);
      const taskDetails = mockTaskDetails[taskCode] || [];
      
      // 将字符串类型的actionType映射到ActionType枚举
      const moveOutItems = taskDetails.filter(item => 
        item.actionType === 'De-Listing' || item.actionType === 'OUT'
      );
      const moveInItems = taskDetails.filter(item => 
        item.actionType === 'New Put-Away' || item.actionType === 'IN'
      );
      const rearrangeItems = taskDetails.filter(item => 
        item.actionType === 'Expand' || item.actionType === 'Shrink' || item.actionType === 'REARRANGE'
      );
      const newItems = taskDetails.filter(item => 
        item.actionType === 'New Put-Away' || item.actionType === 'NEW_ITEM'
      );
      
      const overview = {
        moveOut: {
          totalItems: moveOutItems.length * 10, // 模拟件数
          totalWeight: moveOutItems.length * 2.5, // 模拟重量
          completedCount: moveOutItems.filter(item => item.status === 'completed').length,
          totalCount: moveOutItems.length
        },
        moveIn: {
          totalItems: moveInItems.length * 8,
          totalWeight: moveInItems.length * 2.0,
          completedCount: moveInItems.filter(item => item.status === 'completed').length,
          totalCount: moveInItems.length
        },
        rearrange: {
          totalItems: rearrangeItems.length * 6,
          totalWeight: rearrangeItems.length * 1.5,
          completedCount: rearrangeItems.filter(item => item.status === 'completed').length,
          totalCount: rearrangeItems.length
        },
        newItems: {
          totalItems: newItems.length * 5,
          totalWeight: newItems.length * 1.8,
          completedCount: newItems.filter(item => item.status === 'completed').length,
          totalCount: newItems.length
        }
      };
      return createMockResponse(overview);
    }
    let resp = await httpClient.get(API_ENDPOINTS.TASKS.QUERY_BY_CODE + "?orderCode=" + taskCode + "&warehouseCode=" + shop);
    let order = resp.data.data;
    let moveOutProgress = order.moveOutProgress;
    let moveOut = {
      totalItems: moveOutProgress.plannedSkuCount,
      totalWeight: 0, // 添加缺失的 totalWeight 属性
      completedCount: moveOutProgress.actualSkuCount,
      totalCount: moveOutProgress.plannedSkuCount
    };
    let moveInProgress = order.moveInProgress;
    let moveIn = {
      totalItems: moveInProgress.plannedSkuCount,
      totalWeight: 0, // 添加缺失的 totalWeight 属性
      completedCount: moveInProgress.actualSkuCount,
      totalCount: moveInProgress.plannedSkuCount
    };
    let rearrangeProgress = order.rearrangeProgress;
    let rearrange = {
      totalItems: rearrangeProgress.plannedSkuCount,
      totalWeight: 0, // 添加缺失的 totalWeight 属性
      completedCount: rearrangeProgress.actualSkuCount,
      totalCount: rearrangeProgress.plannedSkuCount
    };
    let newItemProgress = order.newItemProgress;
    let newItems = {
      totalItems: newItemProgress.plannedSkuCount,
      totalWeight: 0, // 添加缺失的 totalWeight 属性
      completedCount: newItemProgress.actualSkuCount,
      totalCount: newItemProgress.plannedSkuCount
    };
    const overview = {
        moveOut: moveOut,
        moveIn: moveIn,
        rearrange: rearrange,
        newItems: newItems
      };
    return {
      traceId: Math.random().toString(36).substr(2, 9),
      success: true,
      errorMessage: null,
      flag:  'success',
      data: overview,
    }
  }

  // 获取下架作业详情
  static async getMoveOutDetails(shop: string, taskCode: string): Promise<ApiResponse<MoveOutDetail[]>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(500);
      const taskDetails = mockTaskDetails[taskCode] || [];
      // 将actionType映射到OUT类型
      const moveOutItems = taskDetails.filter(item => item.actionType === 'OUT');
      
      const details: MoveOutDetail[] = moveOutItems.map((item, index) => ({
        itemCode: `ITEM${String(index + 1).padStart(3, '0')}`,
        itemName: item.item,
        itemSpec: 'Unit' as const,
        currentLayer: `L${item.shelfLevel}`,
        targetCart: `购物车${String.fromCharCode(65 + (index % 3))}`,
        plannedQuantity: item.currentFacing * item.currentDepth,
        status: item.status === 'completed' ? 'Completed' : item.status === 'Exception' ? 'Exception' : 'Pending',
        executor: item.status === 'completed' ? '张三' : undefined,
        completeTime: item.status === 'completed' ? '01-25 10:30' : undefined
      }));
      
      return createMockResponse(details);
    }
    
    let resp = await httpClient.get(API_ENDPOINTS.TASKS.QUERY_OUT_DETAILS + "?orderCode=" + taskCode + "&warehouseCode=" + shop);
    return resp.data;
  }

  // 获取移入作业详情
  static async getMoveInDetails(shop: string, taskCode: string): Promise<ApiResponse<MoveInDetail[]>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(500);
      const taskDetails = mockTaskDetails[taskCode] || [];
      // 将actionType映射到IN类型
      const moveInItems = taskDetails.filter(item => item.actionType === 'IN');
      
      const details: MoveInDetail[] = moveInItems.map((item, index) => ({
        itemCode: `ITEM${String(index + 1).padStart(3, '0')}`,
        itemName: item.item,
        itemSpec: 'Unit' as const,
        targetLayer: `L${item.shelfLevel}`,
        displayOrder: item.displayOrder,
        plannedXFacings: item.targetFacing,
        plannedYFacings: item.targetDepth,
        plannedZFacings: 1,
        actualXFacings: item.currentFacing,
        actualYFacings: item.currentDepth,
        actualZFacings: 1,
        sourceShelf: `货架${String.fromCharCode(65 + (index % 5))}`,
        sourceLayer: `L${item.currentLevel}`,
        status: item.status === 'completed' ? 'Completed' : item.status === 'Exception' ? 'Exception' : 'Pending',
        executor: item.status === 'completed' ? '李四' : undefined
      }));
      
      return createMockResponse(details);
    }
    
    let resp = await httpClient.get(API_ENDPOINTS.TASKS.QUERY_IN_DETAILS + "?orderCode=" + taskCode + "&warehouseCode=" + shop);
    return resp.data;
  }

  // 获取调整作业详情
  static async getRearrangeDetails(shop: string, taskCode: string): Promise<ApiResponse<RearrangeDetail[]>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(500);
      const taskDetails = mockTaskDetails[taskCode] || [];
      // 将actionType映射到REARRANGE类型
      const rearrangeItems = taskDetails.filter(item => item.actionType === 'REARRANGE');
      
      const details: RearrangeDetail[] = rearrangeItems.map((item, index) => ({
        itemCode: `ITEM${String(index + 1).padStart(3, '0')}`,
        itemName: item.item,
        itemSpec: 'Unit' as const,
        targetLayer: `L${item.shelfLevel}`,
        displayOrder: item.displayOrder,
        plannedXFacings: item.targetFacing,
        plannedYFacings: item.targetDepth,
        plannedZFacings: 1,
        currentXFacings: item.currentFacing,
        currentYFacings: item.currentDepth,
        currentZFacings: 1,
        adjustmentType: 'Quantity Increase', // 默认值
        status: item.status === 'completed' ? 'Completed' : item.status === 'Exception' ? 'Exception' : 'Pending',
        executor: item.status === 'completed' ? '王五' : undefined
      }));
      
      return createMockResponse(details);
    }
    
    let resp = await httpClient.get(API_ENDPOINTS.TASKS.QUERY_REARRANGE_DETAILS + "?orderCode=" + taskCode + "&warehouseCode=" + shop);
    return resp.data;
  }

  // 获取新商品作业详情
  static async getNewItemDetails(shop: string, taskCode: string): Promise<ApiResponse<NewItemDetail[]>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(500);
      const taskDetails = mockTaskDetails[taskCode] || [];
      // 将actionType映射到NEW_ITEM类型
      const newItems = taskDetails.filter(item => item.actionType === 'NEW_ITEM');
      
      const details: NewItemDetail[] = newItems.map((item, index) => ({
        itemCode: `ITEM${String(index + 1).padStart(3, '0')}`,
        itemName: item.item,
        itemSpec: 'Unit' as const,
        targetLayer: `L${item.shelfLevel}`,
        displayOrder: item.displayOrder,
        plannedXFacings: item.targetFacing,
        plannedYFacings: item.targetDepth,
        plannedZFacings: 1,
        actualXFacings: item.currentFacing,
        actualYFacings: item.currentDepth,
        actualZFacings: 1,
        sourceLocation: '仓库A区',
        sourceLayer: 'L1',
        status: item.status === 'completed' ? 'Completed' : item.status === 'Exception' ? 'Exception' : 'Pending',
        executor: item.status === 'completed' ? '赵六' : undefined
      }));
      
      return createMockResponse(details);
    }

    let resp = await httpClient.get(API_ENDPOINTS.TASKS.QUERY_NEW_DETAILS + "?orderCode=" + taskCode + "&warehouseCode=" + shop);
    return resp.data;
  }

  // 更新任务状态
  static async updateTaskStatus(shop: string, taskCode: string, status: TaskStatus): Promise<ApiResponse<boolean>> {
    if (USE_MOCK_DATA) {
      await mockDelay(200);
      const task = mockTasks.find(t => t.taskCode === taskCode);
      if (task) {
        task.status = status;
        // 如果状态更新为完成，设置完成时间
        if (status === 'Completed') {
          task.completeTime = new Date().toISOString();
        }
        return createMockResponse(true);
      } else {
        return createMockResponse(false, false, '任务不存在');
      }
    }

    return api.post('/planogram-tasks/update-status', { taskCode, status });
  }

  // 批量更新任务状态
  static async batchUpdateTaskStatus(updates: Array<{ taskCode: string; status: TaskStatus }>): Promise<ApiResponse<{ successCount: number; failedCount: number }>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(300);
      let successCount = 0;
      let failedCount = 0;
      
      updates.forEach(update => {
        const task = mockTasks.find(t => t.taskCode === update.taskCode);
        if (task) {
          task.status = update.status;
          // 如果状态更新为完成，设置完成时间
          if (update.status === 'Completed') {
            task.completeTime = new Date().toISOString();
          }
          successCount++;
        } else {
          failedCount++;
        }
      });
      
      return createMockResponse({ successCount, failedCount });
    }

    return api.post('/planogram-tasks/batch-update-status', { updates });
  }

  // 批量标记任务优先级
  static async batchMarkPriority(taskCodes: string[], priority: Priority): Promise<ApiResponse<{ successCount: number; failedCount: number }>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(300);
      let successCount = 0;
      let failedCount = 0;
      
      taskCodes.forEach(taskCode => {
        const task = mockTasks.find(t => t.taskCode === taskCode);
        if (task) {
          task.priority = priority;
          successCount++;
        } else {
          failedCount++;
        }
      });
      
      return createMockResponse({ successCount, failedCount });
    }

    let res = await httpClient.post(API_ENDPOINTS.TASKS.BATCH_MARK_PRIORITY, { orderCodes: taskCodes, priority });
    return res.data
  }

  // 批量暂停任务
  static async batchPauseTasks(taskCodes: string[], pauseReason: string, pauseRemark?: string): Promise<ApiResponse<{ successCount: number; failedCount: number }>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(300);
      let successCount = 0;
      let failedCount = 0;
      
      taskCodes.forEach(taskCode => {
        const task = mockTasks.find(t => t.taskCode === taskCode);
        if (task && (task.status === 'Not Started' || task.status === 'In Progress')) {
          task.isPaused = true;
          task.pauseReason = pauseReason as PauseReason;
          task.pauseRemark = pauseRemark; // 设置暂停说明
          successCount++;
        } else {
          failedCount++;
        }
      });
      
      return createMockResponse({ successCount, failedCount });
    }

    // 更新 API 调用以包含暂停说明
    let res = await httpClient.post(API_ENDPOINTS.TASKS.BATCH_PAUSE, { 
      orderCodes: taskCodes, 
      pauseReason,
      pauseRemark 
    });
    return res.data
  }

  // 批量恢复任务
  static async batchResumeTasks(taskCodes: string[]): Promise<ApiResponse<{ successCount: number; failedCount: number }>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(300);
      let successCount = 0;
      let failedCount = 0;
      
      taskCodes.forEach(taskCode => {
        const task = mockTasks.find(t => t.taskCode === taskCode);
        if (task && task.isPaused) {
          task.isPaused = false;
          task.pauseReason = undefined;
          successCount++;
        } else {
          failedCount++;
        }
      });
      
      return createMockResponse({ successCount, failedCount });
    }

    let res = await httpClient.post(API_ENDPOINTS.TASKS.BATCH_RESUME, { orderCodes: taskCodes });
    return res.data
  }

  // 批量指派Section Owner
  static async batchAssignSectionOwner(taskCodes: string[], sectionOwner: string): Promise<ApiResponse<{ successCount: number; failedCount: number }>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(300);
      let successCount = 0;
      let failedCount = 0;
      
      taskCodes.forEach(taskCode => {
        const task = mockTasks.find(t => t.taskCode === taskCode);
        if (task && task.status !== 'Completed') {
          task.sectionOwner = sectionOwner;
          successCount++;
        } else {
          failedCount++;
        }
      });
      
      return createMockResponse({ successCount, failedCount });
    }

    let res = await httpClient.post(API_ENDPOINTS.TASKS.BATCH_ASSIGN_OWNER, { orderCodes: taskCodes, workerAccount: sectionOwner });
    return res.data
  }

  // 获取任务反馈数据
  static async getTaskFeedback(shop: string, taskCode: string): Promise<ApiResponse<Feedback[]>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(500);
      
      // 查找对应的任务
      const task = mockTasks.find(t => t.taskCode === taskCode);
      if (!task) {
        return createMockResponse([], false, '任务不存在');
      }
      
      // 模拟反馈数据列表
      const mockFeedbackList: Feedback[] = [
        {
          warehouseCode: task.shop,
          orderCode: task.taskCode,
          itemCode: 'ITEM001',
          divisionCode: 'DIV001',
          divisionName: task.division,
          displayGroupCode: 'DG001',
          displayGroupName: task.displayGroup,
          reason: '商品缺货，无法完成陈列',
          fulfillType: 'Partial',
          feedbackImagesList: [
            'https://zos.alipayobjects.com/rmsportal/jkjgkEfvpUPVyRjUImniVslZfWPnJuuZ.png',
            'https://zos.alipayobjects.com/rmsportal/jkjgkEfvpUPVyRjUImniVslZfWPnJuuZ.png'
          ],
          workerAccount: 'worker001',
          workerName: '张三'
        },
        {
          warehouseCode: task.shop,
          orderCode: task.taskCode,
          itemCode: 'ITEM002',
          divisionCode: 'DIV001',
          divisionName: task.division,
          displayGroupCode: 'DG001',
          displayGroupName: task.displayGroup,
          reason: '货架损坏，需要维修',
          fulfillType: 'Complete',
          feedbackImagesList: [
            'https://zos.alipayobjects.com/rmsportal/jkjgkEfvpUPVyRjUImniVslZfWPnJuuZ.png'
          ],
          workerAccount: 'worker002',
          workerName: '李四'
        }
      ];
      
      return createMockResponse(mockFeedbackList);
    }
    
    // 使用真实的API调用
    let resp = await httpClient.get(API_ENDPOINTS.TASKS.GET_FEEDBACK + "?warehouseCode=" + shop + "&orderCode=" + taskCode);
    return resp.data;
  }


  static async pageTaskFeedback(req): Promise<ApiResponse<any>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(500);
      
      // 模拟反馈数据列表
      const mockFeedbackList: Feedback[] = [
        {
          warehouseCode: 'store-001',
          orderCode: 'TASK001',
          itemCode: 'ITEM001',
          divisionCode: 'DIV001',
          divisionName: 'Dry Food',
          displayGroupCode: 'DG001',
          displayGroupName: 'Display Group 1',
          reason: '商品缺货，无法完成陈列',
          fulfillType: 'Partial',
          feedbackImagesList: [
            'https://zos.alipayobjects.com/rmsportal/jkjgkEfvpUPVyRjUImniVslZfWPnJuuZ.png',
            'https://zos.alipayobjects.com/rmsportal/jkjgkEfvpUPVyRjUImniVslZfWPnJuuZ.png'
          ],
          workerAccount: 'worker001',
          workerName: '张三',
          shelfCode: 'SHELF001',
          seqOnLayer: 1,
          fromLayerCode: 'LAYER001',
          fromLayerNumber: 1,
          action: 'IN',
          planNum: 10,
          plan4x: 2,
          plan4y: 3,
          plan4z: 1,
          actualNum: 8,
          actual4x: 2,
          actual4y: 2,
          actual4z: 1
        },
        {
          warehouseCode: 'store-002',
          orderCode: 'TASK002',
          itemCode: 'ITEM002',
          divisionCode: 'DIV002',
          divisionName: 'Fresh Food',
          displayGroupCode: 'DG002',
          displayGroupName: 'Display Group 2',
          reason: '货架损坏，需要维修',
          fulfillType: 'Complete',
          feedbackImagesList: [
            'https://zos.alipayobjects.com/rmsportal/jkjgkEfvpUPVyRjUImniVslZfWPnJuuZ.png'
          ],
          workerAccount: 'worker002',
          workerName: '李四',
          shelfCode: 'SHELF002',
          seqOnLayer: 2,
          fromLayerCode: 'LAYER002',
          fromLayerNumber: 1,
          action: 'OUT',
          planNum: 15,
          plan4x: 3,
          plan4y: 2,
          plan4z: 2,
          actualNum: 15,
          actual4x: 3,
          actual4y: 2,
          actual4z: 2
        }
      ];
      
      // 根据搜索条件过滤数据
      let filteredData = [...mockFeedbackList];
      
      if (req.warehouseCode) {
        filteredData = filteredData.filter(item => item.warehouseCode === req.warehouseCode);
      }
      
      if (req.orderCode) {
        filteredData = filteredData.filter(item => item.orderCode.includes(req.orderCode));
      }
      
      if (req.itemCode) {
        filteredData = filteredData.filter(item => item.itemCode === req.itemCode);
      }
      
      // 模拟分页
      const pageSize = req.pageSize || 20;
      const currentPage = req.currentPage || 1;
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      return createMockResponse({
        tableData: paginatedData,
        paging: {
          currentPage: currentPage,
          totalCount: filteredData.length,
          pageSize: pageSize
        }
      });
    }
    
    // 使用真实的API调用
    let resp = await httpClient.post(API_ENDPOINTS.TASKS.PAGE_FEEDBACK, req);
    return resp.data;
  }

  static async pageTaskFeedback4Store(req): Promise<ApiResponse<any>> {
    if (USE_MOCK_DATA && WHOLE_USE_MOCK) {
      await mockDelay(500);
      
      // 模拟反馈数据列表
      const mockFeedbackList: Feedback[] = [
        {
          warehouseCode: 'store-001',
          orderCode: 'TASK001',
          itemCode: 'ITEM001',
          divisionCode: 'DIV001',
          divisionName: 'Dry Food',
          displayGroupCode: 'DG001',
          displayGroupName: 'Display Group 1',
          reason: '商品缺货，无法完成陈列',
          fulfillType: 'Partial',
          feedbackImagesList: [
            'https://zos.alipayobjects.com/rmsportal/jkjgkEfvpUPVyRjUImniVslZfWPnJuuZ.png',
            'https://zos.alipayobjects.com/rmsportal/jkjgkEfvpUPVyRjUImniVslZfWPnJuuZ.png'
          ],
          workerAccount: 'worker001',
          workerName: '张三',
          shelfCode: 'SHELF001',
          seqOnLayer: 1,
          fromLayerCode: 'LAYER001',
          fromLayerNumber: 1,
          action: 'IN',
          planNum: 10,
          plan4x: 2,
          plan4y: 3,
          plan4z: 1,
          actualNum: 8,
          actual4x: 2,
          actual4y: 2,
          actual4z: 1
        },
        {
          warehouseCode: 'store-002',
          orderCode: 'TASK002',
          itemCode: 'ITEM002',
          divisionCode: 'DIV002',
          divisionName: 'Fresh Food',
          displayGroupCode: 'DG002',
          displayGroupName: 'Display Group 2',
          reason: '货架损坏，需要维修',
          fulfillType: 'Complete',
          feedbackImagesList: [
            'https://zos.alipayobjects.com/rmsportal/jkjgkEfvpUPVyRjUImniVslZfWPnJuuZ.png'
          ],
          workerAccount: 'worker002',
          workerName: '李四',
          shelfCode: 'SHELF002',
          seqOnLayer: 2,
          fromLayerCode: 'LAYER002',
          fromLayerNumber: 1,
          action: 'OUT',
          planNum: 15,
          plan4x: 3,
          plan4y: 2,
          plan4z: 2,
          actualNum: 15,
          actual4x: 3,
          actual4y: 2,
          actual4z: 2
        }
      ];
      
      // 根据搜索条件过滤数据
      let filteredData = [...mockFeedbackList];
      
      if (req.warehouseCode) {
        filteredData = filteredData.filter(item => item.warehouseCode === req.warehouseCode);
      }
      
      if (req.orderCode) {
        filteredData = filteredData.filter(item => item.orderCode.includes(req.orderCode));
      }
      
      if (req.itemCode) {
        filteredData = filteredData.filter(item => item.itemCode === req.itemCode);
      }
      
      // 模拟分页
      const pageSize = req.pageSize || 20;
      const currentPage = req.currentPage || 1;
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      return createMockResponse({
        tableData: paginatedData,
        paging: {
          currentPage: currentPage,
          totalCount: filteredData.length,
          pageSize: pageSize
        }
      });
    }
    
    // 使用真实的API调用
    let resp = await httpClient.post(API_ENDPOINTS.TASKS.PAGE_FEEDBACK_4_STORE, req);
    return resp.data;
  }

  static async finish(id): Promise<ApiResponse<any>> {
    // 使用真实的API调用
    let resp = await httpClient.post(API_ENDPOINTS.TASKS.FEEDBACK_FINISH, {id});
    return resp.data;
  }

   static async reject(id): Promise<ApiResponse<any>> {
    // 使用真实的API调用
    let resp = await httpClient.post(API_ENDPOINTS.TASKS.FEEDBACK_REJECT, {id});
    return resp.data;
  }
}